import React, { useState } from "react";
import { Card, Tabs, Typography, Space, Row, Col, Button, Tag } from "antd";

const { Title, Paragraph, Text } = Typography;

const ModelTypes = () => {
  const categories = [
    {
      id: "llms",
      title: "Large Language Models",
      shortTitle: "LLMs",
      color: "#8b5cf6",
      description:
        "Massive AI models trained on internet-scale data that can understand and generate human-like text",
      subSections: [
        {
          id: "overview",
          title: "What are LLMs?",
          content:
            "Large Language Models are AI systems trained on trillions of words from the internet. They can understand context, answer questions, write code, create content, and perform complex reasoning tasks. Think of them as having read most of the internet and being able to have conversations about almost anything!",
        },
        {
          id: "prompting",
          title: "Prompt Engineering",
          content:
            "Prompt engineering is the art of writing clear instructions to get the best results from LLMs. It's like learning how to talk to AI effectively - being specific, providing examples, and structuring your requests well can dramatically improve the quality of responses.",
        },
        {
          id: "multimodal",
          title: "Multimodal Models",
          content:
            "Modern LLMs can work with multiple types of data - text, images, audio, and even video. Models like GPT-4V can analyze photos, Gemini can understand videos, and some can even generate images from descriptions. This makes them incredibly versatile!",
        },
        {
          id: "image",
          title: "Image Generation",
          content:
            "Specialized AI models that create images from text descriptions using diffusion technology. Type 'a cute robot painting a sunset' and watch as AI generates unique artwork. These models have revolutionized digital art and design.",
        },
      ],
      companies: [
        {
          name: "OpenAI",
          models: ["GPT-4", "ChatGPT", "DALL-E 3"],
          description: "Leading AI research company behind ChatGPT",
          url: "https://openai.com",
        },
        {
          name: "Google",
          models: ["Gemini", "Bard", "PaLM 2"],
          description: "Search giant with multimodal AI models",
          url: "https://deepmind.google/technologies/gemini/",
        },
        {
          name: "Anthropic",
          models: ["Claude 3.5", "Claude 3"],
          description: "AI safety-focused company with conversational AI",
          url: "https://www.anthropic.com",
        },
        {
          name: "Meta",
          models: ["Llama 3", "Code Llama"],
          description: "Open-source models for research and commercial use",
          url: "https://ai.meta.com/llama/",
        },
        {
          name: "Midjourney",
          models: ["Midjourney v6"],
          description: "Artistic AI image generation platform",
          url: "https://www.midjourney.com",
        },
        {
          name: "Stability AI",
          models: ["Stable Diffusion XL"],
          description: "Open-source AI image generation",
          url: "https://stability.ai",
        },
      ],
      quickFacts: [
        "GPT-4 was trained on ~13 trillion tokens (words)",
        "Training costs can exceed $100M for the largest models",
        "LLMs predict likely text based on patterns, not 'true' knowledge",
        "Can generate code in dozens of programming languages",
        "Understanding prompts is becoming a valuable job skill",
      ],
    },
    {
      id: "slms",
      title: "Small Language Models",
      shortTitle: "SLMs",
      description:
        "Efficient models designed to run on regular computers, laptops, and even smartphones",
      subSections: [
        {
          id: "overview",
          title: "What are SLMs?",
          content:
            "Small Language Models have fewer parameters (typically 1-10 billion) but can still perform many useful tasks. They're optimized to run on consumer hardware like laptops and phones, making AI more accessible and private.",
        },
        {
          id: "benefits",
          title: "Why Use SLMs?",
          content:
            "SLMs offer major advantages: 1) Run locally without internet 2) Much faster responses 3) Better privacy - your data stays on device 4) Lower costs 5) Can be customized for specific tasks. Perfect for businesses and personal projects!",
        },
        {
          id: "use-cases",
          title: "Common Use Cases",
          content:
            "SLMs power mobile apps, offline assistants, specialized business tools, educational software, code completion, and research prototypes. They're ideal when you need AI that works without cloud connections.",
        },
      ],
      companies: [
        {
          name: "Microsoft",
          models: ["Phi-3", "Phi-3 Mini"],
          description: "Compact models that run on smartphones",
          url: "https://www.microsoft.com/en-us/research/blog/phi-3-mini/",
        },
        {
          name: "Google",
          models: ["Gemini Nano"],
          description: "On-device AI for Pixel phones",
          url: "https://blog.google/products/pixel/",
        },
        {
          name: "Apple",
          models: ["Apple Intelligence"],
          description: "On-device models for Siri and iOS features",
          url: "https://machinelearning.apple.com",
        },
        {
          name: "Mistral AI",
          models: ["Mistral 7B"],
          description: "High-performance open models",
          url: "https://mistral.ai",
        },
      ],
      quickFacts: [
        "Can run on regular laptops and even smartphones",
        "10-100x cheaper to run than large models",
        "Better for focused, specific tasks",
        "Your data never leaves your device",
        "Can be fine-tuned in hours, not weeks",
      ],
    },
    {
      id: "tiny",
      title: "Tiny Models",
      shortTitle: "Tiny",
      description:
        "Ultra-small models for microcontrollers, IoT devices, and wearables with minimal power",
      subSections: [
        {
          id: "overview",
          title: "What are Tiny Models?",
          content:
            "Tiny models are designed to run on microcontrollers with as little as 256KB of memory - that's smaller than most photos! They enable AI in devices like smartwatches, sensors, and IoT gadgets that run for years on battery power.",
        },
        {
          id: "applications",
          title: "Real Applications",
          content:
            "Voice commands on smartwatches, gesture recognition on wearables, predictive maintenance in factories, wildlife monitoring cameras, health sensors, smart home devices, and industrial IoT. These models make everyday objects intelligent.",
        },
        {
          id: "challenges",
          title: "Technical Challenges",
          content:
            "Extreme constraints: models must fit in kilobytes, run on milliwatts of power, process data in milliseconds, and often work without internet. Engineers use quantization, pruning, and knowledge distillation to squeeze AI into tiny chips.",
        },
      ],
      companies: [
        {
          name: "Arduino",
          models: ["TinyML Library"],
          description: "Maker-friendly microcontroller AI",
          url: "https://www.arduino.cc/education/tinyml",
        },
        {
          name: "Edge Impulse",
          models: ["Edge AI Platform"],
          description: "Tools for building IoT device AI",
          url: "https://www.edgeimpulse.com",
        },
        {
          name: "TensorFlow",
          models: ["TensorFlow Lite Micro"],
          description: "Google's framework for microcontrollers",
          url: "https://www.tensorflow.org/lite/microcontrollers",
        },
        {
          name: "ARM",
          models: ["Cortex-M with ML"],
          description: "Chips optimized for edge AI",
          url: "https://www.arm.com/solutions/artificial-intelligence",
        },
      ],
      quickFacts: [
        "Some models fit in just 18KB - smaller than this webpage!",
        "Run on microcontrollers costing under $5",
        "Can operate for years on a coin cell battery",
        "No internet connection needed after deployment",
        "Used in billions of IoT devices worldwide",
      ],
    },
    {
      id: "huggingface",
      title: "Hugging Face Hub",
      shortTitle: "Hugging Face",
      description:
        "The 'GitHub of AI' - discover, share, and use over 500,000 AI models for free",
      subSections: [
        {
          id: "overview",
          title: "What is Hugging Face?",
          content:
            "Hugging Face is a platform with over 500,000 AI models you can use for free. Think of it as an app store for AI where anyone can share their models, datasets, and demos. It's the go-to place for AI developers and researchers worldwide.",
        },
        {
          id: "features",
          title: "Key Features",
          content:
            "Model Hub (download any model), Datasets (training data), Spaces (try models in browser), AutoTrain (train without coding), Inference API (use models via code), and a huge community helping each other. Everything you need to work with AI!",
        },
        {
          id: "how-to",
          title: "How to Get Started",
          content:
            "First, search for models by task (translation, image generation, etc.). You can filter the models by license, size, language Then, try them in your browser for free. You can download to your system as well. Watch YouTube videos for more understanding.",
        },
      ],
      companies: [
        {
          name: "Hugging Face",
          models: ["Transformers Library"],
          description: "Open-source AI model platform",
          url: "https://huggingface.co",
        },
        {
          name: "Community",
          models: ["500K+ Models"],
          description: "Thousands of contributors worldwide",
          url: "https://huggingface.co/models",
        },
      ],
      stats: {
        models: "500,000+",
        datasets: "100,000+",
        spaces: "200,000+",
        monthlyUsers: "10M+",
      },
      quickFacts: [
        "Most models are completely free to use",
        "Try models in your browser without coding",
        "Download any model with 2 lines of Python code",
        "Huge community helps with questions",
        "Perfect place to start learning AI",
      ],
    },
  ];

  const renderCategoryContent = (category) => {
    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* All sub-sections as cards */}
        {category.subSections.map((sub) => (
          <Card
            key={sub.id}
            title={<span style={{ color: "#0071e3" }}>{sub.title}</span>}
          >
            <Paragraph style={{ fontSize: "16px", marginBottom: 0 }}>
              {sub.content}
            </Paragraph>
          </Card>
        ))}

        {/* Hugging Face Stats */}
        {category.id === "huggingface" && category.stats && (
          <Row gutter={[16, 16]}>
            {Object.entries(category.stats).map(([key, value]) => (
              <Col xs={12} md={6} key={key}>
                <Card>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "32px",
                        fontWeight: 700,
                        color: "#0071e3",
                      }}
                    >
                      {value}
                    </div>
                    <div
                      style={{ fontSize: "12px", textTransform: "capitalize" }}
                    >
                      {key.replace(/([A-Z])/g, " $1").trim()}
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {/* Companies Section */}
        <Card
          title={<span style={{ color: "#0071e3" }}>Companies & Models</span>}
        >
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {category.companies.map((company, idx) => (
              <div
                key={idx}
                style={{
                  padding: "16px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "16px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <Title level={5} style={{ marginBottom: "8px" }}>
                    {company.name}
                  </Title>
                  <Paragraph style={{ marginBottom: "8px", fontSize: "14px" }}>
                    {company.description}
                  </Paragraph>
                  <Space wrap>
                    {company.models.map((model, i) => (
                      <Tag key={i} color="blue">
                        {model}
                      </Tag>
                    ))}
                  </Space>
                </div>
                <Button
                  type="primary"
                  onClick={() =>
                    window.open(company.url, "_blank", "noopener,noreferrer")
                  }
                >
                  Visit
                </Button>
              </div>
            ))}
          </Space>
        </Card>

        {/* Quick Facts */}
        <Card title={<span style={{ color: "#0071e3" }}>Quick Facts</span>}>
          <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
            {category.quickFacts.map((fact, idx) => (
              <li key={idx} style={{ marginBottom: "8px" }}>
                {fact}
              </li>
            ))}
          </ul>
        </Card>
      </Space>
    );
  };

  const renderComparison = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={<span style={{ color: "#0071e3" }}>Model Size Comparison</span>}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {[
            {
              type: "LLMs",
              params: "100B+ parameters",
              color: "#8b5cf6",
              width: "100%",
              examples: "GPT-4, Gemini Ultra, Claude 3",
            },
            {
              type: "SLMs",
              params: "1B-10B parameters",
              color: "#10b981",
              width: "30%",
              examples: "Phi-3, Gemini Nano, Llama 7B",
            },
            {
              type: "Tiny",
              params: "<100M parameters",
              color: "#f59e0b",
              width: "10%",
              examples: "Micro Speech, TinyML models",
            },
          ].map((item, idx) => (
            <div key={idx}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "1px",
                }}
              >
                <Text strong>{item.type}</Text>
                <Text type="secondary">{item.params}</Text>
              </div>
              <Text
                style={{ fontSize: "12px", fontWeight: 600, color: "gray" }}
              >
                {item.examples}
              </Text>
              <div
                style={{
                  width: "100%",
                  height: "40px",
                  background: "#f5f5f5",
                  borderRadius: "8px",
                  overflow: "hidden",
                  position: "relative",
                  marginTop: "4px",
                }}
              >
                <div
                  style={{
                    width: item.width,
                    height: "100%",
                    background: item.color,
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "16px",
                  }}
                ></div>
              </div>
            </div>
          ))}
        </Space>
      </Card>

      <Card
        title={<span style={{ color: "#0071e3" }}>Feature Comparison</span>}
      >
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  borderBottom: "2px solid #0071e3",
                }}
              >
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    width: "25%",
                  }}
                >
                  <Text strong>Feature</Text>
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                  }}
                >
                  <Text strong style={{ color: "#8b5cf6" }}>
                    LLMs
                  </Text>
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                  }}
                >
                  <Text strong style={{ color: "#10b981" }}>
                    SLMs
                  </Text>
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                  }}
                >
                  <Text strong style={{ color: "#f59e0b" }}>
                    Tiny
                  </Text>
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                {
                  feature: "Model Size",
                  llm: "100B+ params",
                  slm: "1-10B params",
                  tiny: "<100M params",
                },
                {
                  feature: "Hardware",
                  llm: "Cloud servers",
                  slm: "Laptops, phones",
                  tiny: "Microcontrollers",
                },
                {
                  feature: "Internet Required",
                  llm: "Yes",
                  slm: "No",
                  tiny: "No",
                },
                { feature: "Cost", llm: "$$$", slm: "$$", tiny: "$" },
                {
                  feature: "Speed",
                  llm: "Slower",
                  slm: "Fast",
                  tiny: "Very fast",
                },
                {
                  feature: "Privacy",
                  llm: "Cloud-based",
                  slm: "On-device",
                  tiny: "Fully local",
                },
                {
                  feature: "Capabilities",
                  llm: "Broad & deep",
                  slm: "Focused tasks",
                  tiny: "Specific tasks",
                },
              ].map((row, idx) => (
                <tr
                  key={idx}
                  style={{
                    borderBottom: idx < 6 ? "2px solid #f0f0f0" : "none",
                  }}
                >
                  <td
                    style={{
                      padding: "16px",
                      verticalAlign: "top",
                      textAlign: "left",
                    }}
                  >
                    <Text strong style={{ color: "#0071e3" }}>
                      {row.feature}
                    </Text>
                  </td>
                  <td
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      verticalAlign: "top",
                    }}
                  >
                    {row.llm}
                  </td>
                  <td
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      verticalAlign: "top",
                    }}
                  >
                    {row.slm}
                  </td>
                  <td
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      verticalAlign: "top",
                    }}
                  >
                    {row.tiny}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>Remember</span>}>
        <Paragraph style={{ marginBottom: "16px" }}>
          Bigger isn't always better! Smaller models can be highly optimized for
          specific tasks and often work better for focused applications.
        </Paragraph>
      </Card>
    </Space>
  );

  const tabItems = [
    ...categories.map((category) => ({
      key: category.id,
      label: category.shortTitle,
      children: renderCategoryContent(category),
    })),
    {
      key: "compare",
      label: "Compare",
      children: renderComparison(),
    },
  ];

  return (
    <div className="page-container">
      <header
        style={{ textAlign: "center", marginBottom: "32px" }}
        className="fade-in-down"
      >
        <Title level={1}>AI Model Types</Title>
        <Paragraph style={{ fontSize: "18px" }}>
          Explore different sizes of AI models - from giant cloud computers to
          tiny chips
        </Paragraph>
      </header>

      <Tabs defaultActiveKey="llms" items={tabItems} size="large" />
    </div>
  );
};

export default ModelTypes;
